import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Question} from '../../models/underwriter.model';
import {GlobalQuestionService} from '../../services/global-question.service';
import {QuestionFlowSessionQuery} from '../state/question-flow-session.query';
import {environment} from '../../../../../environments/environment';

declare const $: any;

// map styling from the design prototype (questionnaire-add-address.html)
const mapStyles: any[] = [
  {
    featureType: 'all',
    elementType: 'labels.text.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'administrative.neighborhood',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#333333',
      },
    ],
  },
  {
    featureType: 'poi.business',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.business',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.place_of_worship',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.place_of_worship',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [
      {
        visibility: 'simplified',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text',
    stylers: [
      {
        weight: 0.5,
      },
      {
        color: '#333333',
      },
    ],
  },
  {
    featureType: 'transit.station',
    elementType: 'labels.icon',
    stylers: [
      {
        gamma: 1,
      },
      {
        saturation: 50,
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
      {
        saturation: 50,
      },
      {
        hue: '#50a5d1',
      },
    ],
  },
];

@Component({
    selector: 'app-question-address-textbox',
    templateUrl: './question-address-textbox.component.html',
    styleUrls: ['./question-address-textbox.component.scss'],
    standalone: false
})
export class QuestionAddressTextboxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('addressText', {static: false}) addressText: ElementRef;
  @ViewChild('mapCanvas', {static: false}) mapCanvas: ElementRef;
  @ViewChild('modalUnitInput', {static: false}) modalUnitInput: ElementRef;
  @ViewChild('modalComplexInput', {static: false}) modalComplexInput: ElementRef;
  @Input() question: Question;
  @Input() readOnly = false;
  @Input() showLabel = false;
  @Input() labelText = '';
  @Input() address = '';
  @Input() showMap = true;
  @Input() pickLocation = true;
  @Output() questionAnswer = new EventEmitter<any>();
  @Output() pressNext = new EventEmitter<any>();

  map: google.maps.Map = null as any;
  marker: google.maps.Marker = null as any;
  autocomplete: google.maps.places.Autocomplete = null as any;
  autocompleteBoundEl: HTMLElement = null as any;

  apartmentNo = '';
  complex = '';
  streetAddress = '';
  suburb = '';
  province = '';
  city = '';
  postalCode = '';
  gps = '';

  placeIsValid = false;
  showError = false;
  firstFocus = false;
  startedTyping = false;
  mapVisible = false;
  showAdditionalInfo = false;
  showAddDetailsLink = true;
  showUnit = false;
  showComplex = false;
  showPin = true;

  addresses: any = [];
  showAddresses = false;
  session_details: any;
  url = environment.cssURL;
  defaultCenter = {lat: -26.1714402, lng: 28.0050051}; // default to Johannesburg

  constructor(
    private ref: ChangeDetectorRef,
    private globalQuestionService: GlobalQuestionService,
    private questionFlowSessionQuery: QuestionFlowSessionQuery,
  ) {
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.getPlaceAutocomplete();

    // pickLocation mode: show map immediately with a draggable marker
    if (this.pickLocation) {
      this.initPickLocationMap();
    }

    // clear form fields in modal each time it opens
    if (typeof $ !== 'undefined') {
      $('#add-address-details-modal').on('show.bs.modal', () => {
        if (this.modalUnitInput) { this.modalUnitInput.nativeElement.value = ''; }
        if (this.modalComplexInput) { this.modalComplexInput.nativeElement.value = ''; }
      });
    }

    if (this.question?.answer) {
      this.restoreAnswer(this.question.answer);
    } else if (this.address) {
      this.restoreAnswer(this.address);
    } else {
      this.globalQuestionService.spinner(true);
      this.session_details = this.questionFlowSessionQuery.getValue();
      if (!this.question?.cent_aq_question_id) { this.globalQuestionService.spinner(false); return; }
      const parameters = new Map<string, string>();
      parameters.set('session_id', this.session_details?.session_id);
      parameters.set('question_id', `${this.question.cent_aq_question_id}`);
      this.globalQuestionService.runCommandNew('getAddress', parameters).subscribe(addresses => {
        if (addresses.length > 0) {
          this.showAddresses = true;
          const add = [];
          let index = 0;
          addresses.forEach(a => {
            if (add.indexOf(a.cent_answer_data) === -1) {
              try {
                let json = JSON.parse(a.cent_answer_data);
                let address = '';
                if (json.apartmentNo && json.apartmentNo !== '') address += json.apartmentNo + ', ';
                if (json.complex && json.complex !== '') address += json.complex + ', ';
                address += json.streetAddress + ', ';
                address += json.suburb + ', ';
                address += json.province + ', ';
                address += json.city + ', ';
                address += json.postalCode;
                add.push({index: index, address: address, json: a.cent_answer_data});
                index++;
              }
              catch { }
            }
          });

          this.addresses = add;

        }
        this.globalQuestionService.spinner(false);
      });
    }

  }

  ngOnChanges(changes: SimpleChanges): void {
    this.applyQuestionParameters();
    this.getPlaceAutocomplete();
  }

  // question.parameter is a JSON string of {parameter, value} pairs (e.g. showMap/showComplex/showPin)
  // set per-question in the backend - only override a flag when its key is actually present.
  applyQuestionParameters(): void {
    if (!this.question?.parameter) { return; }
    let params: any[];
    try {
      params = JSON.parse(this.question.parameter);
    } catch {
      return;
    }
    const find = (name: string) => params?.find(p => p?.parameter === name)?.value;

    const showMapParam = find('showMap');
    const showComplexParam = find('showComplex');
    const showPinParam = find('showPin');

    if (showMapParam !== undefined) { this.showMap = showMapParam === '1'; }
    if (showComplexParam !== undefined) { this.showAddDetailsLink = showComplexParam === '1'; }
    if (showPinParam !== undefined) { this.showPin = showPinParam === '1'; }
  }

  ngOnDestroy(): void {
    if (typeof $ !== 'undefined') {
      $('#add-address-details-modal').off('show.bs.modal');
      $('#add-address-details-modal').modal('hide');
    }
  }

  // Prefill from a previously saved answer (JSON or legacy plain string) and
  // geocode it so the map, valid state and additional-info section restore.
  restoreAnswer(answer: string): void {
    let csvString = '';
    try {
      const address = JSON.parse(answer);
      this.apartmentNo = address?.apartmentNo || '';
      this.complex = address?.complex || '';
      this.streetAddress = address?.streetAddress || '';
      this.suburb = address?.suburb || '';
      this.province = address?.province || '';
      this.city = address?.city || '';
      this.postalCode = address?.postalCode || '';
      this.gps = address?.gps || '';
      csvString = [this.streetAddress, this.suburb, this.province, this.city, this.postalCode]
        .filter(v => v && v !== '').join(', ');
      if (this.apartmentNo) { this.showUnit = true; }
      if (this.complex) { this.showComplex = true; }
      if (this.apartmentNo || this.complex) { this.showAddDetailsLink = false; }
    }
    catch {
      csvString = answer;
    }
    if (!this.addressText || csvString.trim() === '') { return; }
    this.addressText.nativeElement.value = csvString;

    // stored lat/lng available: show the map directly without a geocode round-trip
    if (this.gps) {
      const parts = this.gps.split(',');
      const pos = {lat: parseFloat(parts[0]), lng: parseFloat(parts[1])};
      if (isFinite(pos.lat) && isFinite(pos.lng)) {
        if (this.showMap || this.pickLocation) {
          this.showMapAtLocation(pos);
        }
        this.showAdditionalInfo = true;
        this.placeIsValid = true;
        this.setValidMsg();
        this.ref.detectChanges();
        return;
      }
    }

    // get the latlng from the address string and show the map
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({address: csvString}, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        const pos = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };
        this.gps = pos.lat + ', ' + pos.lng;
        if (this.showMap || this.pickLocation) {
          this.showMapAtLocation(pos);
        }
        this.showAdditionalInfo = true;
        this.placeIsValid = true;
        this.setValidMsg();
        this.ref.detectChanges();
      } else {
        console.log('Geocode was not successful for the following reason: ' + status);
      }
    });
  }

  getPlaceAutocomplete(): void {
    let ctry = 'ZA';
    if (this.url.includes('india')) {
      ctry = 'IN';
    }
    if (!this.addressText) { return; }
    // the input element is recreated when toggling the stored-address list,
    // so only rebind when it actually changed (avoids stacking instances)
    if (this.autocompleteBoundEl === this.addressText.nativeElement) { return; }
    this.autocompleteBoundEl = this.addressText.nativeElement;

    this.autocomplete = new google.maps.places.Autocomplete(this.addressText.nativeElement, {
      componentRestrictions: {country: ctry}
    });

    this.autocomplete.setFields([
      'address_components',
      'geometry',
      'icon',
      'name',
    ]);

    google.maps.event.addListener(this.autocomplete, 'place_changed', () => {
      this.onPlaceChanged();
    });
  }

  onPlaceChanged(): void {
    const place = this.autocomplete.getPlace();

    // Error: no address components
    if (!place?.address_components) {
      console.warn('No details available for input: \'' + place?.name + '\'');
      this.placeIsValid = false;
      this.setValidMsg();
      if (!this.pickLocation) {
        this.mapVisible = false;
      }
      this.showAdditionalInfo = false;
      this.gps = '';
      this.ref.detectChanges();
      return;
    }

    // Success: place has valid address components
    this.placeIsValid = true;
    this.setValidMsg();

    const pos = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };

    if (this.showMap || this.pickLocation) {
      this.showMapAtLocation(pos);
    }
    this.showAdditionalInfo = true;

    // store GPS coords in hidden input
    this.gps = pos.lat + ', ' + pos.lng;

    // pickLocation mode: sync marker to the autocomplete-selected place
    if (this.pickLocation && this.marker) {
      this.marker.setPosition(pos);
      this.map.setCenter(pos);
      this.map.setZoom(15);
    }

    this.populateAddress(place.address_components);
    this.ref.detectChanges();
  }

  // on first focus of address input, attempt to geolocate user & automatically populate address
  onInputFocus(e: any): void {
    const target = e.target as HTMLInputElement;
    target?.setAttribute('autocomplete', 'one-time-code');

    if (!this.firstFocus) {
      this.firstFocus = true;

      // don't geolocate over an address that's already valid (e.g. prepopulated/restored)
      if (this.placeIsValid) {
        return;
      }

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {

          // if accuracy is greater than 50m, don't use it
          if (position.coords.accuracy > 50) {
            console.log('Location accuracy too low, won\'t prepopulate address');
            return;
          }

          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          this.prefillFromPosition(pos);
        });
      }
    }
  }

  prefillFromPosition(pos: any): void {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({location: pos}, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {

        // don't override if user already started typing, or a valid address is already set
        if (this.startedTyping || this.placeIsValid) {
          console.log('User started typing or a valid address is already set, won\'t overwrite');
          return;
        }

        this.addressText.nativeElement.value = results[0].formatted_address;

        // open autocomplete dropdown based on populated address
        const event = new Event('input', {bubbles: true});
        this.addressText.nativeElement.dispatchEvent(event);

      } else {
        console.log('Error, cannot determine the address at this location');
      }
    });
  }

  // When the user leaves the input, if no valid place was selected
  // then show an error state on the input's form-group.
  onInputBlur(): void {
    this.setValidMsg();
  }

  // If user types manually, clear any previously stored GPS coords and hide map unless pickLocation keeps it visible
  onInputChanged(): void {
    this.startedTyping = true;
    this.placeIsValid = false;
    this.gps = '';
    if (!this.pickLocation) {
      this.mapVisible = false;
    }
    this.showAdditionalInfo = false;
  }

  setValidMsg(): void {
    this.showError = !this.placeIsValid;
  }

  initPickLocationMap(): void {
    this.mapVisible = true;
    this.ref.detectChanges();
    if (!this.mapCanvas) { return; }

    this.map = new google.maps.Map(this.mapCanvas.nativeElement, {
      center: this.defaultCenter,
      zoom: 5,
      styles: mapStyles,
      mapTypeControl: false,
      streetViewControl: false
    });

    this.marker = new google.maps.Marker({
      map: this.showPin ? this.map : null,
      draggable: true,
      icon: this.markerImage(),
      position: this.defaultCenter,
    });

    // Try to geolocate user on init and move marker to their position.
    // If the user has already selected a valid address, don't override it.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (this.placeIsValid) { return; }
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          this.map.setCenter(pos);
          this.map.setZoom(18);
          this.marker.setPosition(pos);
          this.geocodePositionAndPopulate(pos);
        },
        (error) => {
          console.log('Geolocation failed or denied, marker left at default position');
        },
        {enableHighAccuracy: true, timeout: 10000}
      );
    }

    // On drag end, reverse geocode the new position and populate address fields.
    // Reset startedTyping so the input value is always updated when the user drags.
    google.maps.event.addListener(this.marker, 'dragend', () => {
      this.startedTyping = false;
      this.geocodePositionAndPopulate(this.marker.getPosition());
    });
  }

  // Reverse geocode a position and populate the address input + GPS coords
  geocodePositionAndPopulate(pos: any): void {
    const lat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
    const lng = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({location: {lat, lng}}, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        const place = results[0];

        if (!this.startedTyping) {
          this.addressText.nativeElement.value = place.formatted_address;
        }
        this.gps = lat + ', ' + lng;
        this.showAdditionalInfo = true;
        this.placeIsValid = true;
        this.setValidMsg();
        this.populateAddress(place.address_components);
        this.ref.detectChanges();
      } else {
        console.log('Error, cannot determine the address at this location');
      }
    });
  }

  showMapAtLocation(pos: any): void {
    // pickLocation mode: map already initialised, just pan and reposition marker
    if (this.pickLocation && this.map) {
      this.map.setCenter(pos);
      this.map.setZoom(15);
      if (this.marker) { this.marker.setPosition(pos); }
      this.mapVisible = true;
      return;
    }

    this.mapVisible = true;
    this.ref.detectChanges();
    if (!this.mapCanvas) { return; }

    this.map = new google.maps.Map(this.mapCanvas.nativeElement, {
      center: pos,
      zoom: 15,
      styles: mapStyles,
      mapTypeControl: false,
      streetViewControl: false
    });

    this.marker = new google.maps.Marker({
      position: pos,
      map: this.showPin ? this.map : null,
      icon: this.markerImage(),
    });
  }

  markerImage(): any {
    return {
      url: 'assets/3/icn/marker.png',
      size: new google.maps.Size(77, 54),
      scaledSize: new google.maps.Size(77, 54),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(20, 50)
    };
  }

  // adding additional address details from modal to main form
  submitDetailsModal(e: any): void {
    e.preventDefault();
    const unitValue = (this.modalUnitInput?.nativeElement.value || '').trim();
    const complexValue = (this.modalComplexInput?.nativeElement.value || '').trim();

    if (unitValue) {
      this.apartmentNo = unitValue;
      this.showUnit = true;
    }
    if (complexValue) {
      this.complex = complexValue;
      this.showComplex = true;
    }

    this.showAddDetailsLink = false;
    if (typeof $ !== 'undefined') {
      $('#add-address-details-modal').modal('hide');
    }

    if (unitValue || complexValue) {
      this.emitAnswer();
    }
    this.ref.detectChanges();
  }

  onUnitChanged(e: any): void {
    this.apartmentNo = e.target.value;
  }

  onComplexChanged(e: any): void {
    this.complex = e.target.value;
  }

  populateAddress(addressComponents: any): void {
    let addressStreetNumber;
    let addressStreetName;
    this.streetAddress = '';
    this.suburb = '';
    this.city = '';
    this.province = '';
    this.postalCode = '';

    for (const item of addressComponents) {
      // street incl. number
      if (item.types.indexOf('street_number') !== -1) {
        addressStreetNumber = item.short_name;
      }

      // Street Name
      if (item.types.indexOf('route') !== -1) {
        addressStreetName = item.long_name;
      }

      // Street Number
      if (addressStreetNumber) {
        // has both name and number
        if (addressStreetName) {
          this.streetAddress = `${addressStreetNumber} ${addressStreetName}`;
          // only number (even possible?)
        } else {
          this.streetAddress = addressStreetNumber;
        }
        // only name
      } else if (addressStreetName) {
        this.streetAddress = addressStreetName;
      }


      // suburb
      if ((item.types.indexOf('sublocality') !== -1
          || item.types.indexOf('sublocality_level_1') !== -1
          || item.types.indexOf('sublocality_level_2') !== -1)
        && item.types.indexOf('political') !== -1) {
        if (!this.suburb) {
          this.suburb = item.short_name;
        }
      }

      // city
      if (item.types.indexOf('locality') !== -1 && item.types.indexOf('political') !== -1) {
        this.city = item.short_name;
      }

      // province
      if (item.types.indexOf('administrative_area_level_1') !== -1 && item.types.indexOf('political') !== -1) {
        this.province = item.short_name;
      }

      // postal code
      if (item.types.indexOf('postal_code') !== -1) {
        this.postalCode = item.short_name;
      }
    }
    this.ref.detectChanges();

    this.emitAnswer();
  }

  emitAnswer(): void {
    const address = {
      apartmentNo: (this.apartmentNo || '').trim(),
      complex: (this.complex || '').trim(),
      streetAddress: this.streetAddress || '',
      suburb: this.suburb || '',
      province: this.province || '',
      city: this.city || '',
      postalCode: this.postalCode || '',
      gps: this.gps || ''
    };

    if (this.question) {
      this.question.answer = JSON.stringify(address);
      this.questionAnswer.emit(this.question);
    } else {
      this.questionAnswer.emit(JSON.stringify(address));
    }
  }

  setAddress(event): void {
    if (event === 'new') {
      this.showAddresses = false;
      this.ref.detectChanges();
      this.getPlaceAutocomplete();
      return;
    }
    this.question.answer = this.addresses[event.index]?.json;

    if (this.showAddresses) {
      this.questionAnswer.emit(this.question);
    }
  }

}
